import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "context/UserContext";
import { DASHBOARD_PATH } from "global/constants";
import {
    authErrorMessage,
    beginTotpSetup,
    cognitoConfigured,
    completeNewPassword,
    confirmEmail,
    confirmPasswordReset,
    clearCachedSignIn,
    keepCachedSignIn,
    confirmSmsMfaSetup,
    confirmTotpSetup,
    continuePendingSignIn,
    mfaIsConfigured,
    releaseCredentials,
    resendPhoneVerification,
    startSmsMfaSetup,
    requestPasswordReset,
    resendEmailCode,
    takePasswordReset,
    selectMfa,
    sessionIdentity,
    signIn,
    signUp,
    submitMfaCode,
} from "services/cognitoAuth";

export const AUTH_STEP = {
    SIGN_IN: "SIGN_IN",
    CONFIRM_EMAIL: "CONFIRM_EMAIL",
    SELECT_MFA: "SELECT_MFA",
    SETUP_MFA: "SETUP_MFA",
    SMS_SETUP: "SMS_SETUP",
    SMS_CODE: "SMS_CODE",
    TOTP_CODE: "TOTP_CODE",
    TOTP_SETUP: "TOTP_SETUP",
    NEW_PASSWORD: "NEW_PASSWORD",
    RESET_REQUEST: "RESET_REQUEST",
    RESET_CODE: "RESET_CODE",
};

const SMS_SETUP_ERROR = "auth.error.smsSetup";

export default function useCognitoAuth() {
    const [, setToken] = useContext(UserContext);
    const navigate = useNavigate();
    const [queuedResetEmail] = useState(() => takePasswordReset());
    const [step, setStep] = useState(
        queuedResetEmail === null ? AUTH_STEP.SIGN_IN : AUTH_STEP.RESET_REQUEST
    );
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [busy, setBusy] = useState(false);
    const [methods, setMethods] = useState(["SMS", "TOTP"]);
    const [destination, setDestination] = useState("");
    const [accountEmail, setAccountEmail] = useState(queuedResetEmail || "");
    const [qrCode, setQrCode] = useState("");
    const [secret, setSecret] = useState("");
    const [mustConfigureMfa, setMustConfigureMfa] = useState(false);

    const holdForMfaSetup = async (result) => {
        if (result?.step !== "DONE") {
            return false;
        }
        clearCachedSignIn();
        if (await mfaIsConfigured()) {
            keepCachedSignIn();
            return false;
        }
        setMethods(["SMS", "TOTP"]);
        setMustConfigureMfa(true);
        setDestination("");
        setStep(AUTH_STEP.SETUP_MFA);
        return true;
    };

    const configError = cognitoConfigured()
        ? ""
        : "auth.error.notConfigured";

    const apply = (result) => {
        if (!result) {
            return;
        }
        if (result.step === "DONE") {
            if (!result.session) {
                setError("auth.error.noSession");
                return;
            }
            releaseCredentials();
            const identity = sessionIdentity(result.session);
            setToken(identity.accessToken);
            navigate(DASHBOARD_PATH);
            return;
        }
        if (result.methods) {
            setMethods(result.methods);
        }
        setDestination(result.destination || "");
        setStep(result.step);
    };

    const run = async (action) => {
        if (busy) {
            return;
        }
        setBusy(true);
        setError("");
        setNotice("");
        try {
            await action();
        } catch (err) {
            if (err?.code === "UserNotConfirmedException" || err?.name === "UserNotConfirmedException") {
                setStep(AUTH_STEP.CONFIRM_EMAIL);
                return;
            }
            setError(authErrorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const signInWithPassword = (email, password) => run(async () => {
        setAccountEmail(email.trim());
        const result = await signIn(email, password);
        if (await holdForMfaSetup(result)) {
            return;
        }
        apply(result);
    });

    const register = (payload) => run(async () => {
        setAccountEmail(payload.email.trim());
        const result = await signUp(payload);
        if (result.step === "CONFIRMED") {
            apply(await continuePendingSignIn());
            return;
        }
        setStep(AUTH_STEP.CONFIRM_EMAIL);
    });

    const confirmSignUp = (code) => run(async () => {
        await confirmEmail(code);
        apply(await continuePendingSignIn());
    });

    const resendCode = () => run(async () => {
        await resendEmailCode();
        setNotice("auth.notice.confirmationSent");
    });

    const chooseMfa = (method) => run(async () => {
        apply(await selectMfa(method));
    });

    const chooseSetup = (method) => run(async () => {
        if (method === "SMS") {
            setStep(AUTH_STEP.SMS_SETUP);
            return;
        }
        const setup = await beginTotpSetup(accountEmail);
        setSecret(setup.secret);
        setQrCode(setup.qrCode);
        setStep(AUTH_STEP.TOTP_SETUP);
    });

    const sendSmsSetup = (phone) => run(async () => {
        if (mustConfigureMfa) {
            const result = await startSmsMfaSetup(phone);
            setDestination(result.destination || "");
            setStep(AUTH_STEP.SMS_CODE);
            return;
        }
        const result = await continuePendingSignIn();
        if (result.step === "SETUP_MFA") {
            setMethods(result.methods?.length ? result.methods : ["SMS", "TOTP"]);
            setStep(AUTH_STEP.SETUP_MFA);
            setError(SMS_SETUP_ERROR);
            return;
        }
        apply(result);
    });

    const confirmCode = (code, method) => run(async () => {
        if (mustConfigureMfa && method === "SMS") {
            apply(await confirmSmsMfaSetup(code));
            return;
        }
        apply(await submitMfaCode(code, method));
    });

    const resendSmsCode = () => run(async () => {
        if (mustConfigureMfa) {
            const result = await resendPhoneVerification();
            setDestination(result.destination || destination);
            setNotice("auth.notice.smsSent");
            return;
        }
        const result = await continuePendingSignIn();
        if (result.step === "SMS_CODE") {
            setDestination(result.destination || destination);
            setNotice("auth.notice.smsSent");
            return;
        }
        apply(result);
    });

    const confirmTotp = (code) => run(async () => {
        apply(await confirmTotpSetup(code));
    });

    const submitNewPassword = (password) => run(async () => {
        const result = await completeNewPassword(password);
        if (await holdForMfaSetup(result)) {
            return;
        }
        apply(result);
    });

    const backToMfaChoice = () => {
        setStep(AUTH_STEP.SETUP_MFA);
        setError("");
        setNotice("");
        setQrCode("");
        setSecret("");
    };

    const sendResetCode = (email) => run(async () => {
        setAccountEmail(email.trim());
        const result = await requestPasswordReset(email);
        setDestination(result.destination || "");
        setStep(AUTH_STEP.RESET_CODE);
    });

    const submitReset = (code, password) => run(async () => {
        await confirmPasswordReset(code, password);
        setStep(AUTH_STEP.SIGN_IN);
        setNotice("auth.notice.passwordUpdated");
    });

    const goToSignIn = () => {
        setStep(AUTH_STEP.SIGN_IN);
        setError("");
        setNotice("");
        setQrCode("");
        setSecret("");
    };

    const goToReset = () => {
        setStep(AUTH_STEP.RESET_REQUEST);
        setError("");
        setNotice("");
    };

    const clearFeedback = () => {
        setError("");
        setNotice("");
    };

    return {
        step,
        error: error || configError,
        notice,
        busy,
        methods,
        destination,
        accountEmail,
        qrCode,
        secret,
        mustConfigureMfa,
        signInWithPassword,
        register,
        confirmSignUp,
        resendCode,
        chooseMfa,
        chooseSetup,
        sendSmsSetup,
        confirmCode,
        resendSmsCode,
        confirmTotp,
        submitNewPassword,
        sendResetCode,
        submitReset,
        goToSignIn,
        backToMfaChoice,
        goToReset,
        clearFeedback,
    };
}
